import { ApiService } from './api.service';

/**
 * Language mapping for display names
 * Sorted alphabetically (except 'original' which is always first)
 */
export const SUPPORTED_LANGUAGES = {
  original: 'Original (No Translation)',
  arabic: 'Arabic',
  chinese: 'Chinese (Simplified)',
  dutch: 'Dutch',
  english: 'English',
  french: 'French',
  german: 'German',
  hindi: 'Hindi',
  italian: 'Italian',
  japanese: 'Japanese',
  korean: 'Korean',
  portuguese: 'Portuguese',
  russian: 'Russian',
  spanish: 'Spanish',
  turkish: 'Turkish',
} as const;

/**
 * Service for translating text using the backend API
 */
export class TranslationService {
  /**
   * Translate text to a target language
   * @param text - The text to translate
   * @param targetLanguage - The target language
   * @returns Promise<string> - The translated text
   */
  async translateText(text: string, targetLanguage: string): Promise<string> {
    try {
      // Skip translation if target is 'original' or empty text
      if (targetLanguage === 'original' || !text.trim()) {
        return text;
      }

      const response = await ApiService.post<{ translatedText: string }>(
        'ai/assembly-ai/translate',
        {
          text,
          targetLanguage:
            SUPPORTED_LANGUAGES[targetLanguage as keyof typeof SUPPORTED_LANGUAGES] ||
            targetLanguage,
        }
      );

      return response.translatedText;
    } catch (error) {
      console.error('[TranslationService] Translation failed:', error);
      // Return original text on error
      return text;
    }
  }

  /**
   * Check if translation is needed for a given language
   * @param targetLanguage - The target language
   * @returns boolean - true if translation is needed
   */
  isTranslationNeeded(targetLanguage: string): boolean {
    return targetLanguage !== 'original' && !!targetLanguage;
  }
}

// Singleton instance
export const translationService = new TranslationService();
